// The expanded body of a rule row: an IF block listing every condition on its
// own monospace line and a THEN block listing every action. Each block has a
// coloured left border. Rendered inside the row's MUI Collapse when the user
// expands the rule from the chevron next to its name.
//
// The text comes from `logic/auto-transaction-summary`, the same renderer the
// tile view uses. This file used to carry its own near-identical copy, which
// had drifted: it printed raw ULIDs where the tile printed display names, and
// every new action variant had to be added twice.

import { Box, Stack, Typography } from "@mui/material";
import type { AutoTransactionRuleDto } from "../../../../api/types";
import { useRuleSetNames } from "../../../../hooks/useRuleSetNames";
import {
  summarizeActions,
  summarizeCondition,
} from "../../../../logic/auto-transaction-summary";

const MonoLine = ({ text }: { text: string }) => (
  <Typography
    component="div"
    variant="body2"
    sx={{
      color: "text.secondary",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: "0.78rem",
      lineHeight: 1.45,
    }}
  >
    {text}
  </Typography>
);

const Block = ({
  label,
  color,
  borderColor,
  lines,
}: {
  label: string;
  color: string;
  borderColor: string;
  lines: string[];
}) => (
  <Box>
    <Typography
      component="div"
      variant="caption"
      sx={{ fontWeight: 700, color, letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Box sx={{ borderLeft: 2, borderColor, pl: 1, ml: 0.5, mt: 0.25 }}>
      {lines.map((line, i) => (
        <MonoLine key={i} text={line} />
      ))}
    </Box>
  </Box>
);

export const StackedRuleBody = ({ rule }: { rule: AutoTransactionRuleDto }) => {
  const resolvers = useRuleSetNames();
  const conditionLines = summarizeCondition(rule.condition, resolvers)
    .full.split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);
  const actionLines = rule.actions.length
    ? summarizeActions(rule.actions, resolvers)
        .full.split("\n")
        .map((line) => `• ${line.trim()}`)
    : [];

  return (
    <Stack component="div" spacing={0.5}>
      <Block
        label="IF"
        color="info.dark"
        borderColor="info.light"
        lines={conditionLines}
      />
      {actionLines.length > 0 && (
        <Block
          label="THEN"
          color="success.dark"
          borderColor="success.light"
          lines={actionLines}
        />
      )}
    </Stack>
  );
};
